import { useEffect, useState, type ComponentType } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Boxes,
  Box,
  Bug,
  Cog,
  CreditCard,
  ChevronDown,
  FileText,
  KeyRound,
  LayoutDashboard,
  PackageSearch,
  QrCode,
  Settings,
  ShieldCheck,
  Users,
  Wallet,
  Waypoints,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import StatusBadge from '../components/ui/StatusBadge';
import { cn } from '../lib/utils';

type MatchMode = 'exact' | 'prefix';

interface NavItem {
  label: string;
  path: string;
  icon: ComponentType<{ className?: string }>;
  match?: MatchMode;
  badge?: string;
  activePaths?: string[];
  activePrefixes?: string[];
}

interface NavSection {
  title: string;
  items: NavItem[];
}

interface SidebarNavProps {
  onNavigate?: () => void;
}

type SectionExpandedState = Record<string, boolean>;

const isItemActive = (pathname: string, item: NavItem): boolean => {
  if (item.activePaths?.includes(pathname)) {
    return true;
  }

  if (item.activePrefixes?.some((prefix) => pathname.startsWith(prefix))) {
    return true;
  }

  if (item.match === 'prefix') {
    return pathname.startsWith(item.path);
  }
  return pathname === item.path;
};

const buildDefaultExpandedState = (sections: NavSection[]): SectionExpandedState =>
  sections.reduce((state, section) => {
    state[section.title] = true;
    return state;
  }, {} as SectionExpandedState);

const loadExpandedState = (storageKey: string, sections: NavSection[]): SectionExpandedState => {
  const fallback = buildDefaultExpandedState(sections);
  if (typeof window === 'undefined') {
    return fallback;
  }

  try {
    const stored = window.sessionStorage.getItem(storageKey);
    if (!stored) {
      return fallback;
    }

    const parsed = JSON.parse(stored) as Record<string, unknown>;
    return sections.reduce((state, section) => {
      state[section.title] =
        typeof parsed[section.title] === 'boolean' ? (parsed[section.title] as boolean) : true;
      return state;
    }, {} as SectionExpandedState);
  } catch {
    return fallback;
  }
};

const adminSections: NavSection[] = [
  {
    title: 'CORE OPERATIONS',
    items: [
      { label: 'Dashboard', path: '/admin', icon: LayoutDashboard },
      { label: 'Credit Management', path: '/admin/credit-management', icon: Wallet },
      { label: 'Operation Costs', path: '/admin/operation-costs', icon: CreditCard },
    ],
  },
  {
    title: 'TRACEABILITY',
    items: [
      { label: 'QR Lifecycle', path: '/admin/qr-lifecycle', icon: QrCode },
      { label: 'Verification Logs', path: '/admin/verification-logs', icon: Activity },
      { label: 'Consumption Logs', path: '/admin/consumption-logs', icon: FileText },
      { label: 'Anomaly Flags', path: '/admin/anomaly/flags', icon: AlertTriangle, badge: 'AI' },
      { label: 'Anomaly Policy', path: '/admin/anomaly/policy', icon: ShieldCheck },
      { label: 'Audit Trail', path: '/admin/product-info-audit', icon: FileText },
    ],
  },
  {
    title: 'COMPLIANCE',
    items: [
      { label: 'Compliance', path: '/admin/compliance', icon: ShieldCheck },
      { label: 'Recall Management', path: '/admin/recall-management', icon: AlertTriangle },
    ],
  },
  {
    title: 'SYSTEM',
    items: [
      { label: 'Roles & Permissions', path: '/admin/roles-permissions', icon: Users },
      { label: 'Trial Management', path: '/admin/trial-management', icon: Settings },
      { label: 'Issue Inbox', path: '/admin/issue-reports', icon: Bug },
    ],
  },
];

const manufacturerSections: NavSection[] = [
  {
    title: 'CORE OPERATIONS',
    items: [
      {
        label: 'Batch Management',
        path: '/products/create-batch',
        icon: Boxes,
        activePrefixes: ['/products/batch/', '/packaging/config/'],
      },
      { label: 'Credit Management', path: '/manufacturer/credit-management', icon: Wallet },
      { label: 'Product Registry', path: '/products', icon: Box },
      { label: 'QR Labels', path: '/package-assignments', icon: PackageSearch },
      { label: 'Packaging Hierarchy', path: '/hierarchy/config', icon: Waypoints },
      { label: 'Dashboard', path: '/manufacturer', icon: LayoutDashboard },
    ],
  },
  {
    title: 'ACCESS',
    items: [
      { label: 'Operations Managers', path: '/operations-managers', icon: Users },
      { label: 'API Access', path: '/api-access', icon: KeyRound },
    ],
  },
  {
    title: 'TRACEABILITY',
    items: [
      { label: 'Analytics', path: '/analytics', icon: BarChart3 },
      { label: 'QR Lifecycle', path: '/qr-lifecycle', icon: QrCode },
      { label: 'Verification Logs', path: '/verification-logs', icon: Activity },
      { label: 'Consumption Logs', path: '/consumption-logs', icon: FileText },
      { label: 'Anomaly Flags', path: '/anomaly-flags', icon: AlertTriangle, badge: 'AI' },
    ],
  },
  {
    title: 'SYSTEM',
    items: [
      { label: 'Report Issues', path: '/manufacturer/issue-reports', icon: Bug },
      { label: 'Users', path: '/users/manage', icon: Users },
      { label: 'Settings', path: '/settings', icon: Cog },
    ],
  },
];

export default function SidebarNav({ onNavigate }: SidebarNavProps) {
  const location = useLocation();
  const { user } = useAuthStore();

  const isAdmin = user?.roleLevel === -1;
  const sections = isAdmin ? adminSections : manufacturerSections;
  const storageKey = `pureaum.sidebar.sections.${isAdmin ? 'admin' : 'manufacturer'}`;
  const [expandedSections, setExpandedSections] = useState<SectionExpandedState>(() =>
    loadExpandedState(storageKey, sections)
  );

  useEffect(() => {
    setExpandedSections(loadExpandedState(storageKey, sections));
  }, [storageKey, sections]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(expandedSections));
    } catch {
      // Ignore storage errors and keep in-memory toggle behavior.
    }
  }, [expandedSections, storageKey]);

  useEffect(() => {
    const activeSection = sections.find((section) =>
      section.items.some((item) => isItemActive(location.pathname, item))
    );
    if (!activeSection) {
      return;
    }

    setExpandedSections((prev) => {
      if (prev[activeSection.title] !== false) {
        return prev;
      }
      return { ...prev, [activeSection.title]: true };
    });
  }, [location.pathname, sections]);

  const toggleSection = (title: string) => {
    setExpandedSections((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  return (
    <div className="flex h-full w-full flex-col border-r border-gray-200 bg-white">
      <div className="flex h-16 items-center border-b border-gray-200 px-5">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-600 text-white">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">Pureaum</p>
            <p className="text-xs text-gray-500">Enterprise Console</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="Primary navigation">
        <div className="space-y-6">
          {sections.map((section) => (
            <section key={section.title}>
              {(() => {
                const sectionId = `sidebar-section-${(isAdmin ? 'admin' : 'manufacturer')}-${section.title
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, '-')}`;
                const expanded = expandedSections[section.title] ?? true;

                return (
                  <>
                    <button
                      type="button"
                      onClick={() => toggleSection(section.title)}
                      className="flex w-full items-center justify-between rounded-md px-2 pb-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600"
                      aria-expanded={expanded}
                      aria-controls={sectionId}
                    >
                      <span>{section.title}</span>
                      <ChevronDown
                        className={cn('h-3.5 w-3.5 transition-transform', expanded ? 'rotate-0' : '-rotate-90')}
                      />
                    </button>
                    {expanded && (
                      <div id={sectionId} className="space-y-1">
                        {section.items.map((item) => {
                          const Icon = item.icon;
                          const active = isItemActive(location.pathname, item);
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={onNavigate}
                              aria-current={active ? 'page' : undefined}
                              className={cn(
                                'group flex items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                                active
                                  ? 'bg-emerald-50 text-emerald-800'
                                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                              )}
                            >
                              <span className="flex items-center gap-2.5">
                                <Icon
                                  className={cn(
                                    'h-4 w-4',
                                    active ? 'text-emerald-700' : 'text-gray-500 group-hover:text-gray-700'
                                  )}
                                />
                                <span>{item.label}</span>
                              </span>
                              {item.badge && (
                                <StatusBadge tone={item.badge === 'AI' ? 'ai-flagged' : 'anchored'} size="sm">
                                  {item.badge}
                                </StatusBadge>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </section>
          ))}
        </div>
      </nav>
    </div>
  );
}
